import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

export function ContextualQuestions({ t, questions, onDone }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState(() => questions.map(() => ""));
  const inputRef = useRef(null);

  const q = questions[idx];
  const isLast = idx === questions.length - 1;

  useEffect(() => {
    const id = setTimeout(() => inputRef.current?.focus(), 60);
    return () => clearTimeout(id);
  }, [idx]);

  const set = (v) => setAnswers((p) => p.map((a, i) => (i === idx ? v : a)));

  const finish = () => {
    onDone(questions.map((question, i) => ({ q: question, a: answers[i] })).filter((x) => (x.a || "").trim()));
  };

  const advance = () => {
    if (isLast) return finish();
    setIdx((i) => i + 1);
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      advance();
    }
  };

  const num = String(idx + 1).padStart(2, "0");
  const total = String(questions.length).padStart(2, "0");

  return (
    <motion.div
      data-testid="contextual-questions"
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: EASE }}
      className="relative z-10 w-full max-w-xl mx-auto text-center"
    >
      <div className="mb-8 text-xs tracking-[0.25em] text-bh-c2">
        {t.questions.label} <span className="mx-1">·</span> {num} <span className="mx-1">—</span> {total}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={idx}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.5, ease: EASE }}
        >
          {idx === 0 && <p className="mb-5 text-sm text-bh-soft">{t.questions.intro}</p>}
          <h3 className="font-display text-2xl md:text-3xl tracking-tight">{q}</h3>

          <div className="mt-10">
            <textarea
              ref={inputRef}
              data-testid="contextual-answer"
              rows={2}
              value={answers[idx]}
              onChange={(e) => set(e.target.value)}
              onKeyDown={onKey}
              className="no-scrollbar w-full resize-none bg-transparent border-b border-bh-line py-2 text-center text-xl md:text-2xl outline-none focus:border-bh-ink transition-colors duration-300"
            />
          </div>
        </motion.div>
      </AnimatePresence>

      <div className="mt-10 flex items-center justify-center gap-8">
        <button
          data-testid="contextual-skip"
          onClick={advance}
          className="text-sm text-bh-c2 hover:text-bh-ink transition-colors"
        >
          {t.intake.skip}
        </button>
        <button
          data-testid="contextual-next-btn"
          onClick={advance}
          className="group inline-flex items-center gap-3 text-lg"
        >
          <span className="relative">
            {isLast ? t.questions.done : t.intake.next}
            <span className="absolute left-0 -bottom-1 h-px w-full bg-bh-ink origin-left scale-x-100 group-hover:scale-x-0 transition-transform duration-500" />
            <span className="absolute left-0 -bottom-1 h-px w-full bg-bh-ink origin-right scale-x-0 group-hover:scale-x-100 transition-transform duration-500 delay-100" />
          </span>
          <span className="transition-transform duration-500 group-hover:translate-x-2">→</span>
        </button>
      </div>
    </motion.div>
  );
}
